import { createUrnUtil } from 'urn-lib'
import fetch, { getUrlObject } from '../libs/fetch'
import { getFrameworkServiceUrl, getFrameworkVar } from './states'
import { BROWSER_ROLE, IAM_SERVICE_URL_KEY } from './constants'
import { indexArray } from './utils'
import { IamPermissionSetAccessType } from './enums'
import {
  PREDEFINED_ROLES_DEF_KEY,
  cache,
} from './cache'
import type {
  AccessObj,
  IamAccess,
  IamBaseRoleDefinition,
  IamCustomRoleDefinition,
  IamPermissionSet,
  IamPermissionSetDefinition,
  IamPermissionSets,
  IamPredefinedRoleDefinition,
  IamRolePermissionDefinition,
  IamScopeOptions,
  IndexedArray,
  ReadonlyAccess,
  StringArraySet,
} from './stateTypes'

export { createUrnUtil }
export const prn = createUrnUtil('prn', {
  components: [
    'tsg_id',
    'app_id',
    'region_id',
    'instance_id',
    'resource_scope',
  ],
  separator: ':',
  allowEmpty: true,
})

const DEFAULT_EMPTY_SCOPE = {
  app_id: null,
  region: null,
  instance_id: null,
  resource_scope: null,
}

const createStringArraySet = (strArrOrSet: string[] | Set<string> | null | undefined): StringArraySet => {
  if (!strArrOrSet) {
    return Object.defineProperties([] as string[], {
      has: { value: () => false }
    }) as StringArraySet
  }
  const strSet = Array.isArray(strArrOrSet) ? new Set(strArrOrSet as string[]) : strArrOrSet
  return Object.defineProperties(Array.from(strSet), {
    has: { value: strSet.has.bind(strSet) }
  }) as StringArraySet
}

export const parseAccess = (
  access: IamAccess | undefined = {},
  rolePermissionDefinition?: IamRolePermissionDefinition,
) => {
  const prns = Object.keys(access).filter(acc => acc.startsWith('prn:'))
  const mergePermissionSets = (roles: IamBaseRoleDefinition[], includeChildren?: boolean) => {
    const mergeAccessTypes = (newValues: string[], existingValues?: string[]) => {
      return Array.from(new Set(
        newValues.concat(existingValues || []).filter(Boolean)
      )).sort()
    }
    const permissionSetsMap = roles.reduce((map, role) => {
      if (role.permission_sets?.length) {
        for (const permissionSet of role.permission_sets) {
          const existing = map.get(permissionSet.id)
          const permSetDef = rolePermissionDefinition?.permissionSets?.get(permissionSet.id)
          let permSetAccessTypes: string[]
          if (!existing) {
            const permSet = permSetDef ? Object.create(permSetDef) : {}
            Object.assign(permSet, permissionSet)
            map.set(permSet.id, permSet)
            if (permSet.name && permSet.name !== permSet.id) {
              map.set(permSet.name, permSet)
            }
            permSetAccessTypes = permSet.access_types || []
          }
          else if (permissionSet.access_types?.length) { // merge access_types
            const existingAccessTypeSet = new Set(existing.access_types)
            const hasNewAccessType = permissionSet.access_types.length > existingAccessTypeSet.size ||
              permissionSet.access_types.some(accessType => !existingAccessTypeSet.has(accessType))
            if (!hasNewAccessType) {
              continue // skip merge
            }
            permSetAccessTypes = mergeAccessTypes(permissionSet.access_types, existing.access_types)
            existing.access_types = permSetAccessTypes
          }
          if (includeChildren && permSetDef?.childrenPermissionSets?.length) {
            const addChildrenRecursive = (childrenPermissionSets: IamPermissionSetDefinition[]) => {
              for (const childDef of childrenPermissionSets) {
                const existingChild = map.get(childDef.name)
                if (existingChild) {
                  existingChild.access_types = mergeAccessTypes(permSetAccessTypes, existingChild.access_types)
                }
                const childPermSet = Object.assign(Object.create(childDef), {
                  id: childDef.name,
                  access_types: Array.from(permSetAccessTypes), // clone
                })
                map.set(childPermSet.id, childPermSet)
                if (childDef.childrenPermissionSets?.length) {
                  addChildrenRecursive(childDef.childrenPermissionSets)
                }
              }
            }
            addChildrenRecursive(permSetDef.childrenPermissionSets)
          }
        }
      }
      return map
    }, new Map<string, IamPermissionSet>())
    const permissionSets = Object.defineProperties(Array.from(permissionSetsMap.values()), {
      get: { value: permissionSetsMap.get.bind(permissionSetsMap) },
      has: { value: permissionSetsMap.has.bind(permissionSetsMap) },
      // _map: { value: permissionSetsMap },
    }) as IamPermissionSets
    return permissionSets
  }
  const getPermissionSetChildren = (parentName: string) => {
    //Base Case
    const parent = rolePermissionDefinition?.permissionSets.find((e: any) => e.name === parentName)
    if (!parent?.children?.length) {
      return []
    }
    //Recursive Call
    const children = parent.children
    for (const child of parent.children) {
      children.push.apply(getPermissionSetChildren(child))
    }
    return children
  }
  const filterUserPermissionSets = ({ scopes: { app_id, instance_id, region, resource_scope } }: IamScopeOptions = {
    scopes: DEFAULT_EMPTY_SCOPE
  }) => {
    const userPermissionSets: any = {}
    for (const resource of accessArray) {
      if ((resource.app_id && !resource.app_id.includes(`${app_id}`)) ||
        (resource.region_id && !resource.region_id.includes(`${region}`)) ||
        (resource.instance_id && !resource.instance_id.includes(`${instance_id}`)) ||
        (resource.resource_scope && !resource.resource_scope.includes(`${resource_scope}`))) {
        continue
      }
      for (const permissionSet of resource?.aggPermissionSets) {
        if (!userPermissionSets[permissionSet.id]) userPermissionSets[permissionSet.id] = []
        if (permissionSet.access_types) userPermissionSets[permissionSet.id].push(...permissionSet.access_types)
      }
    }
    return userPermissionSets
  }
  const map = new Map()
  const accessArray = prns.map((prnKey: string) => {
    const parsed = prn.parse(prnKey)
    const roleNames = access[prnKey]
    const roles = roleNames.map(name => (
      rolePermissionDefinition?.roles?.get(name) || rolePermissionDefinition?.customRoles?.get(name) || { name }
    ) as IamBaseRoleDefinition)
    const permissions = createStringArraySet(roles.flatMap(role => role.permissions).filter(Boolean) as string[])
    const permissionSets = mergePermissionSets(roles)
    const acc = {
      resource: prnKey,
      ...parsed,
      roleNames,
      roles,
      permissions,
      permissionSets,
    } as AccessObj
    map.set(prnKey, acc)
    return acc
  })
  let tsgScope: AccessObj | undefined
  for (const scope of accessArray) {
    const splitedScope = scope.resource.replace(/:+$/, '').split(':', 6)
    if (splitedScope.length === 3) { // app level
      const aggRoles = new Set(scope.roleNames)
      const aggPermissions = new Set(scope.permissions)
      const aggRolesArrays = Array.from(scope.roles)
      splitedScope.pop()
      const parentScopeRes = map.get(splitedScope.fill('', splitedScope.length, 6).join(':'))
      const parentScope = map.get(parentScopeRes)
      if (parentScope) {
        parentScope.roles.forEach(aggRolesArrays.concat, aggRolesArrays)
        parentScope.roleNames.forEach(aggRoles.add, aggRoles)
        parentScope.permissions.forEach(aggPermissions.add, aggPermissions)
      }
      scope.aggRoleNames = createStringArraySet(aggRoles)
      scope.aggPermissions = createStringArraySet(aggPermissions)
      scope.aggPermissionSets = mergePermissionSets(aggRolesArrays, true)
    }
    else if (splitedScope.length === 2) { // tsg level
      tsgScope = scope
      scope.aggRoleNames = scope.roleNames
      scope.aggPermissions = scope.permissions
      scope.aggPermissionSets = mergePermissionSets(scope.roles, true)
    }
  }
  const hasRoleCache = {} as Record<string, boolean>
  const hasRole = (roleName: string, appIdOrScope?: string): boolean => {
    const cacheKey = `${appIdOrScope || '*'}:${roleName}`
    if (hasRoleCache.hasOwnProperty(cacheKey)) {
      return hasRoleCache[cacheKey]
    }
    let scopes = accessArray
    if (appIdOrScope) {
      if (appIdOrScope.startsWith('prn:')) {
        const scope = map.get(appIdOrScope) || tsgScope
        scopes = scope ? [scope] : []
      }
      else {
        scopes = accessArray.filter(
          scope => (scope.app_id === appIdOrScope && !scope.resource_scope)
        )
      }
      if (tsgScope && !scopes.length) {
        scopes.push(tsgScope)
      }
    }
    const found = scopes.some(scope => (scope.aggRoleNames || scope.roleNames)?.includes(roleName))
    hasRoleCache[cacheKey] = found
    return found
  }
  const hasRoleOnly = (roleName: string): boolean => {
    if (!accessArray.length || accessArray.length > 2) {
      return false
    }
    const cacheKey = `*:${roleName}|only`
    if (hasRoleCache.hasOwnProperty(cacheKey)) {
      return hasRoleCache[cacheKey]
    }
    const hasOnlyTheRoleIgnoreBrower = () => {
      const excludedRole = roleName !== BROWSER_ROLE ? BROWSER_ROLE : ''
      const roleNameSet = new Set<string>()
      for (const scope of accessArray) {
        for (const name of scope.roleNames) {
          if (name !== excludedRole) {
            roleNameSet.add(name)
            if (roleNameSet.size > 1) {
              return false
            }
          }
        }
      }
      return roleNameSet.size === 1 && roleNameSet.has(roleName)
    }
    const found = hasOnlyTheRoleIgnoreBrower()
    hasRoleCache[cacheKey] = found
    return found
  }
  const checkPermissionSet = (permissionSetName: string, options: IamScopeOptions = {
    scopes: DEFAULT_EMPTY_SCOPE
  }): IamPermissionSetAccessType => {
    const userPermissionSets = filterUserPermissionSets(options)
    if (userPermissionSets[permissionSetName]?.includes('write')) {
      return IamPermissionSetAccessType.WRITE
    }
    if (userPermissionSets[permissionSetName]?.includes('read')) {
      return IamPermissionSetAccessType.READ
    }
    return IamPermissionSetAccessType.NO_ACCESS
  }
  const hasPermissionSetChildren = (permissionSetName: string, options: IamScopeOptions = {
    scopes: {
      app_id: null,
      region: null,
      instance_id: null,
      resource_scope: null
    }
  }): boolean => {
    const userPermissionSets = filterUserPermissionSets(options)
    if (userPermissionSets[permissionSetName]) {
      return true
    }
    const inheritedPermissionSets = getPermissionSetChildren(permissionSetName)
    for (const childPermissionSet of inheritedPermissionSets) {
      if (userPermissionSets[childPermissionSet]) {
        return true
      }
    }
    return false
  }
  const noRoleAccess = !accessArray.length || accessArray.every(scope => !scope.roleNames?.length || (
    scope.roleNames.length === 1 && scope.roleNames[0] === BROWSER_ROLE
  )) // no roles or only browser roles

  Object.assign(accessArray, access) // keep the original data backward compat
  Object.defineProperties(accessArray, {
    has: { value: map.has.bind(map) },
    get: { value: map.get.bind(map) },
    tsg: { value: tsgScope },
    noRoleAccess: { value: noRoleAccess },
    hasRole: { value: hasRole },
    hasRoleOnly: { value: hasRoleOnly },
    generatePrn: { value: prn.format.bind(prn) },
    checkPermissionSet: { value: checkPermissionSet },
    hasPermissionSetChildren: { value: hasPermissionSetChildren },
  })
  return Object.freeze(accessArray) as ReadonlyAccess
}

const getIamUrl = (path: string) => {
  const baseUrl = getFrameworkServiceUrl(IAM_SERVICE_URL_KEY)
  if (!baseUrl) {
    throw new Error('iam service url is not avaiable')
  }
  const urlObj = getUrlObject({
    baseUrl,
    path: `/${path}`,
  })!
  return urlObj.toString()
}
const fetchIam = <T>(urlPath: string, headers: Record<string, string>): Promise<IndexedArray<T>> => fetch(getIamUrl(urlPath), {
  method: 'GET',
  timeout: 10000, // 10s
  headers,
}).then(async (res) => {
  if (res.ok) {
    const data = await res.json()
    if (data.count >= 0 && Array.isArray(data?.items)) {
      return indexArray<T>(data.items, 'name') as IndexedArray<T>
    }
  }
  const data = await res.json().catch(() => res.text())
  console.error('failed to fetch iam roles', data)
  return indexArray<T>([], 'name') as IndexedArray<T>
}, (error: Error) => {
  console.error('failed to fetch iam roles', error)
  return indexArray<T>([], 'name') as IndexedArray<T>
})
const linkPermissionSetDefs = (permissionSetDefs: IndexedArray<IamPermissionSetDefinition>) => {
  for (const def of permissionSetDefs) {
    const childrenPermissionSets = def.children ? indexArray<IamPermissionSetDefinition>(def.children.map(name =>
      permissionSetDefs.get(name)
    ) || [], 'name') : undefined

    Object.defineProperties(def, {
      parentPermissionSet: def.parent ? {
        get: () => permissionSetDefs.get(def.parent!)
      } : { value: undefined },
      childrenPermissionSets: {
        value: childrenPermissionSets
      },
    })
  }
  return permissionSetDefs
}
export const fetchIamRolePermissionDefinition = async (jwt: string | undefined): Promise<IamRolePermissionDefinition> => {
  if (!jwt) {
    throw new Error('JWT token is required to get IAM roles')
  }
  const ftLocalCache = getFrameworkVar('local_roles_cache')
  const fetchAndCache = async () => {
    const headers = {
      Authorization: `Bearer ${jwt}`,
    }
    const [roles, permissionSets] = await Promise.all([
      fetchIam<IamPredefinedRoleDefinition>('roles', headers),
      fetchIam<IamPermissionSetDefinition>('permission_sets', headers).then(linkPermissionSetDefs),
      // fetchIam<IamPermissionDefinition>('permissions', headers),
    ])
    if (ftLocalCache) {
      await cache.set(PREDEFINED_ROLES_DEF_KEY, {
        roles,
        permissionSets,
      }, { ttl: 3600 }) // in sec
    }
    return { roles, permissionSets }
  }
  const promise = fetchAndCache()
  if (ftLocalCache) {
    const cached = await cache.get(PREDEFINED_ROLES_DEF_KEY)
    if (cached) {
      return {
        roles: indexArray<IamPredefinedRoleDefinition>(cached.roles, 'name'),
        permissionSets: linkPermissionSetDefs(indexArray<IamPermissionSetDefinition>(cached.permissionSets, 'name')),
      }
    }
  }
  return promise
}
export const indexCustomRoles = (customRoles: IamCustomRoleDefinition[] | IndexedArray<IamCustomRoleDefinition>) => {
  if (!(customRoles as any).get) {
    indexArray(customRoles, 'name')
  }
  const map = (customRoles as any)._map
  for (const customRole of customRoles) {
    if (customRole.id) {
      map.set(customRole.id, customRole)
    }
  }
  return customRoles as IndexedArray<IamCustomRoleDefinition>
}
export const fetchIamCustomRoleDefinitionByJwt = async (jwt: string | undefined): Promise<IndexedArray<IamCustomRoleDefinition>> => {
  if (!jwt) {
    throw new Error('JWT token is required to get IAM custom roles')
  }
  const customRoles = await fetchIam<IamCustomRoleDefinition>('custom_roles', {
    Authorization: `Bearer ${jwt}`,
  })
  return indexCustomRoles(customRoles)
}
